/**
 * Calendar fairness & quality analytics for a round-robin schedule.
 *
 * Pure & deterministic (no DB, no time/random) — mirrors the style of
 * `lib/seasonTransition/suggestGroupAssignments.ts`. Everything is derived from
 * the `pairings` array, so it runs on a not-yet-committed preview exactly like
 * the modal's grid/matrix views.
 *
 * IMPORTANT framing — what the schedule can and cannot influence:
 *   In a DOUBLE round-robin every team visits every other team's venue exactly
 *   once, so under the round-trip-per-matchday travel model the *total* away
 *   distance per team is fixed by the group's geography and does NOT change with
 *   the fixture order. Treat the distance section as GROUP geographic fairness
 *   (is the grouping itself balanced?), not as a knob the generator can turn.
 *   The order-dependent levers — the ones a better schedule actually improves —
 *   are home/away streaks and breaks.
 *   For a SINGLE leg the draw does affect who you visit, so distance is then
 *   partly schedule-dependent; the engine handles both transparently.
 */

import { haversineKm, type LatLng } from "../geo/haversine";
import { normalizeWilaya } from "../seasonTransition/wilayaAdjacency";

// ── thresholds (tunable) ─────────────────────────────────────────────────────
const AWAY_STREAK_WARN = 3;
const AWAY_STREAK_DANGER = 4;

export interface AnalyzePairing {
  leg: number;
  game_day: number;
  match_date: string | null;
  home_team_id: string;
  away_team_id: string;
}

export interface AnalyzeOptions {
  legs: number;
  roundsPerLeg: number;
  /** Pass-through of values the generator already computed (not recomputed). */
  stadiumSwaps?: number;
  stadiumUnresolved?: number;
}

export type FairnessSeverity = "info" | "warn" | "danger";

export interface FairnessFlag {
  metric: string;
  severity: FairnessSeverity;
  teamId?: string;
  /** Hardcoded-French fallback; renderers should prefer messageKey. */
  message: string;
  messageKey: string;
  messageParams?: Record<string, unknown>;
}

export interface TeamScheduleStats {
  teamId: string;
  homeCount: number;
  awayCount: number;
  maxAwayStreak: number;
  maxHomeStreak: number;
  /** Count of adjacent same-venue fixtures in this team's sequence. */
  breaks: number;
  /** Split of `breaks` by venue: 2 home games in a row vs 2 away in a row. */
  homeBreaks: number;
  awayBreaks: number;
  /** Round-trip km summed over away matches (null if this team has no coords). */
  totalTravelKm: number | null;
  avgTripKm: number | null;
  longestTripKm: number | null;
  tripsCounted: number;
  tripsMissingCoords: number;
}

export interface DistanceSummary {
  coverage: { teamsWithCoords: number; teamsTotal: number };
  min: number | null;
  max: number | null;
  mean: number | null;
  stdev: number | null;
  mostIsolatedTeamId: string | null;
  longestSingleTrip: { fromTeamId: string; toTeamId: string; km: number } | null;
}

export interface HomeAwaySummary {
  totalBreaks: number;
  maxAwayStreak: number;
  maxHomeStreak: number;
  worstAwayStreakTeamIds: string[];
}

export interface DerbySummary {
  available: boolean;
  /** Same-wilaya fixtures across all legs. */
  sameWilayaPairings: number;
  /** Unique same-wilaya team pairs (legs collapsed). */
  distinctSameWilayaPairs: number;
}

export interface CalendarAnalysis {
  teams: TeamScheduleStats[];
  distance: DistanceSummary;
  homeAway: HomeAwaySummary;
  derbies: DerbySummary;
  stadiumSharing: { swaps: number; unresolved: number };
  flags: FairnessFlag[];
  meta: {
    teamCount: number;
    matchCount: number;
    legs: number;
    roundsPerLeg: number;
    hasDates: boolean;
  };
}

const round = (n: number, d = 0) => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

export function analyzeCalendar(
  pairings: AnalyzePairing[],
  coordsByTeamId: Map<string, LatLng | null>,
  wilayaByTeamId: Map<string, string | null>,
  opts: AnalyzeOptions,
): CalendarAnalysis {
  // Fixture order: game_day then leg (deterministic).
  const ordered = [...pairings].sort(
    (a, b) => a.game_day - b.game_day || a.leg - b.leg,
  );

  const teamIds = Array.from(
    new Set(pairings.flatMap((p) => [p.home_team_id, p.away_team_id])),
  ).sort();

  // Per-team venue sequence ('H'/'A') in fixture order + the opponent for travel.
  type Appearance = { venue: "H" | "A"; opponentId: string; date: string | null };
  const seq = new Map<string, Appearance[]>();
  for (const id of teamIds) seq.set(id, []);
  for (const p of ordered) {
    seq.get(p.home_team_id)!.push({ venue: "H", opponentId: p.away_team_id, date: p.match_date });
    seq.get(p.away_team_id)!.push({ venue: "A", opponentId: p.home_team_id, date: p.match_date });
  }

  const coordOf = (id: string) => coordsByTeamId.get(id) ?? null;

  const teams: TeamScheduleStats[] = [];
  let longestSingleTrip: DistanceSummary["longestSingleTrip"] = null;

  for (const id of teamIds) {
    const apps = seq.get(id)!;
    let homeCount = 0, awayCount = 0, breaks = 0, homeBreaks = 0, awayBreaks = 0;
    let maxAwayStreak = 0, maxHomeStreak = 0, curAway = 0, curHome = 0;

    const home = coordOf(id);
    let tripsCounted = 0, tripsMissingCoords = 0, totalTravelKm = 0, longestTripKm = 0;

    let prevVenue: "H" | "A" | null = null;

    for (const a of apps) {
      if (a.venue === "H") {
        homeCount++; curHome++; curAway = 0;
        maxHomeStreak = Math.max(maxHomeStreak, curHome);
      } else {
        awayCount++; curAway++; curHome = 0;
        maxAwayStreak = Math.max(maxAwayStreak, curAway);
        // Travel: round trip to the opponent's home venue.
        const opp = coordOf(a.opponentId);
        if (home && opp) {
          const trip = haversineKm(home, opp);
          totalTravelKm += 2 * trip;
          longestTripKm = Math.max(longestTripKm, trip);
          tripsCounted++;
          if (!longestSingleTrip || trip > longestSingleTrip.km) {
            longestSingleTrip = { fromTeamId: id, toTeamId: a.opponentId, km: round(trip, 1) };
          }
        } else {
          tripsMissingCoords++;
        }
      }
      if (prevVenue !== null && prevVenue === a.venue) {
        breaks++;
        if (a.venue === "H") homeBreaks++;
        else awayBreaks++;
      }
      prevVenue = a.venue;
    }

    const total = home ? round(totalTravelKm) : null;
    teams.push({
      teamId: id,
      homeCount,
      awayCount,
      maxAwayStreak,
      maxHomeStreak,
      breaks,
      homeBreaks,
      awayBreaks,
      totalTravelKm: total,
      avgTripKm: home && tripsCounted ? round(totalTravelKm / 2 / tripsCounted) : null,
      longestTripKm: home && tripsCounted ? round(longestTripKm) : null,
      tripsCounted,
      tripsMissingCoords,
    });
  }

  // ── distance summary (group geography) ─────────────────────────────────────
  const totals = teams.map((t) => t.totalTravelKm).filter((n): n is number => n != null);
  const teamsWithCoords = teamIds.filter((id) => coordOf(id) != null).length;
  let distMean: number | null = null, distStdev: number | null = null;
  if (totals.length) {
    distMean = totals.reduce((s, n) => s + n, 0) / totals.length;
    distStdev = Math.sqrt(totals.reduce((s, n) => s + (n - distMean!) ** 2, 0) / totals.length);
  }
  const mostIsolated = teams
    .filter((t) => t.totalTravelKm != null)
    .sort((a, b) => (b.totalTravelKm! - a.totalTravelKm!))[0];

  const distance: DistanceSummary = {
    coverage: { teamsWithCoords, teamsTotal: teamIds.length },
    min: totals.length ? round(Math.min(...totals)) : null,
    max: totals.length ? round(Math.max(...totals)) : null,
    mean: distMean != null ? round(distMean) : null,
    stdev: distStdev != null ? round(distStdev) : null,
    mostIsolatedTeamId: mostIsolated?.teamId ?? null,
    longestSingleTrip,
  };

  // ── home/away summary ──────────────────────────────────────────────────────
  const worstAwayStreakTeamIds = teams
    .filter((t) => t.maxAwayStreak >= AWAY_STREAK_WARN)
    .map((t) => t.teamId);
  const homeAway: HomeAwaySummary = {
    totalBreaks: teams.reduce((s, t) => s + t.breaks, 0),
    maxAwayStreak: Math.max(0, ...teams.map((t) => t.maxAwayStreak)),
    maxHomeStreak: Math.max(0, ...teams.map((t) => t.maxHomeStreak)),
    worstAwayStreakTeamIds,
  };

  const hasDates = ordered.some((p) => !!p.match_date);

  // ── derbies (same-wilaya) ──────────────────────────────────────────────────
  const normWil = new Map<string, string>();
  let derbyWilayaKnown = 0;
  for (const id of teamIds) {
    const w = wilayaByTeamId.get(id);
    if (w) { normWil.set(id, normalizeWilaya(w)); derbyWilayaKnown++; }
  }
  let sameWilayaPairings = 0;
  const distinctPairs = new Set<string>();
  for (const p of ordered) {
    const wh = normWil.get(p.home_team_id), wa = normWil.get(p.away_team_id);
    if (wh && wa && wh === wa) {
      sameWilayaPairings++;
      distinctPairs.add([p.home_team_id, p.away_team_id].sort().join("|"));
    }
  }
  const derbies: DerbySummary = {
    available: derbyWilayaKnown > 0,
    sameWilayaPairings,
    distinctSameWilayaPairs: distinctPairs.size,
  };

  // ── flags ──────────────────────────────────────────────────────────────────
  const flags: FairnessFlag[] = [];
  for (const t of teams) {
    if (t.maxAwayStreak >= AWAY_STREAK_DANGER) {
      flags.push({ metric: "away_streak", severity: "danger", teamId: t.teamId, message: `${t.maxAwayStreak} matchs à l'extérieur consécutifs`, messageKey: "calendar.flags.away_streak", messageParams: { count: t.maxAwayStreak } });
    } else if (t.maxAwayStreak >= AWAY_STREAK_WARN) {
      flags.push({ metric: "away_streak", severity: "warn", teamId: t.teamId, message: `${t.maxAwayStreak} matchs à l'extérieur consécutifs`, messageKey: "calendar.flags.away_streak", messageParams: { count: t.maxAwayStreak } });
    }
  }
  if (teamsWithCoords < teamIds.length) {
    flags.push({ metric: "coords_coverage", severity: "info", message: `Distances calculées sur ${teamsWithCoords}/${teamIds.length} équipes (coordonnées de stade manquantes).`, messageKey: "calendar.flags.coords_coverage", messageParams: { have: teamsWithCoords, total: teamIds.length } });
  }
  const unresolved = opts.stadiumUnresolved ?? 0;
  if (unresolved > 0) {
    flags.push({ metric: "stadium_unresolved", severity: "danger", message: `${unresolved} conflit(s) de stade non résolu(s) — à vérifier manuellement.`, messageKey: "calendar.flags.stadium_unresolved", messageParams: { count: unresolved } });
  }

  return {
    teams,
    distance,
    homeAway,
    derbies,
    stadiumSharing: { swaps: opts.stadiumSwaps ?? 0, unresolved },
    flags,
    meta: {
      teamCount: teamIds.length,
      matchCount: pairings.length,
      legs: opts.legs,
      roundsPerLeg: opts.roundsPerLeg,
      hasDates,
    },
  };
}
